// TeX → Unicode normalisation for rendered assistant text (Right Arrow #340).
//
// THE BUG: the model emits inline LaTeX in chat — measured in prod, the
// dominant form is `$\rightarrow$` between two figures during a financial
// analysis ("21,40 € pagados $\rightarrow$ 17,68 € netos"), plus `$\times$`,
// `\cdot`, `\approx`, `\div`. NOTHING in this product renders LaTeX: the web
// bubble is react-markdown + remark-gfm with no math plugin, and the WhatsApp /
// Telegram converters are markdown→platform-markup. So the raw command reaches
// the user verbatim and reads as an escape artifact.
//
// The real fix is upstream (the system instruction now forbids TeX and asks for
// Unicode symbols). This module is the RENDERING SAFETY NET for anything that
// slips through — and for the messages already stored in history.
//
// It is deliberately CONSERVATIVE. Two rules keep it from ever mangling text it
// does not fully understand:
//
//   1. Code is untouchable — fenced blocks and inline code spans are carved out
//      before anything is rewritten, so a snippet that legitimately contains
//      `\times` or `$…$` is returned byte-identical.
//   2. A `$…$` / `\(…\)` wrapper is only unwrapped when its content is JUST
//      handled symbols: it must contain at least one command from the table
//      below, no command outside it, and no math STRUCTURE (`{}`, `^`, `_`).
//      So `$\frac{a}{b}$` and `$x_1 \to x_2$` are left exactly as they are
//      (better an honest raw formula than a scrambled one), and a plain price
//      range like "$5 to $10" is never touched, having no command at all.

// Single-glyph commands with no arguments. Longest-first so a prefix can never
// win (`\leq` before `\le`); the trailing lookahead is the real guard.
const TEX_SYMBOLS = [
    ['leftrightarrow', '↔'],
    ['Leftrightarrow', '⇔'],
    ['rightarrow', '→'],
    ['Rightarrow', '⇒'],
    ['leftarrow', '←'],
    ['Leftarrow', '⇐'],
    ['approx', '≈'],
    ['times', '×'],
    ['cdot', '·'],
    ['euro', '€'],
    ['neq', '≠'],
    ['leq', '≤'],
    ['geq', '≥'],
    ['div', '÷'],
    ['to', '→'],
    ['pm', '±'],
    ['ne', '≠'],
    ['le', '≤'],
    ['ge', '≥'],
];

const SYMBOL_MAP = new Map(TEX_SYMBOLS);
const COMMAND_RE = new RegExp(
    `\\\\(${TEX_SYMBOLS.map(([name]) => name).join('|')})(?![a-zA-Z])`,
    'g'
);
// `\%` is an escaped literal, not a command — handled separately so it can't
// collide with the letter lookahead above.
const ESCAPED_PERCENT_RE = /\\%/g;

// Any backslash-command at all — used to decide whether a wrapper's content is
// fully understood.
const ANY_COMMAND_RE = /\\[a-zA-Z]+/;
// Math structure we refuse to unwrap around.
const MATH_STRUCTURE_RE = /[{}^_]/;

// Fenced blocks (``` or ~~~) and inline code spans, kept as split delimiters so
// they survive untouched. Fences are matched first so a backtick inside one is
// not mistaken for an inline span.
const CODE_SEGMENT_RE = /(```[\s\S]*?```|~~~[\s\S]*?~~~|`[^`\n]*`)/g;

/** Replace every handled command with its Unicode glyph. */
const replaceCommands = (text) =>
    text
        .replace(COMMAND_RE, (_m, name) => SYMBOL_MAP.get(name))
        .replace(ESCAPED_PERCENT_RE, '%');

/**
 * May this inline-math wrapper be removed?
 *
 * Only when the content is nothing but symbols we handle: at least one of them
 * (so ordinary prose and currency are never unwrapped), none we don't, and no
 * `{}`/`^`/`_` structure that would lose meaning without its delimiters.
 */
function unwrappable(inner) {
    if (!inner || MATH_STRUCTURE_RE.test(inner)) return false;
    const replaced = replaceCommands(inner);
    if (replaced === inner) return false;          // nothing of ours in there
    return !ANY_COMMAND_RE.test(replaced);          // nothing we don't know left
}

/** Unwrap `$…$` / `\(…\)` whose content is entirely handled symbols. */
function unwrapInlineMath(text) {
    return text
        .replace(/\$([^$\n]+)\$/g, (m, inner) => (unwrappable(inner) ? replaceCommands(inner) : m))
        .replace(/\\\(([^\n]*?)\\\)/g, (m, inner) => (unwrappable(inner) ? replaceCommands(inner) : m));
}

/**
 * Normalise TeX symbols in a markdown SOURCE string before it is rendered.
 *
 * Idempotent: the output contains none of the patterns it rewrites, so running
 * it twice changes nothing. Returns the input unchanged (byte-identical) when
 * there is no TeX to normalise — which is the overwhelmingly common case, so
 * the cheap early-out below is also the correctness guarantee for normal text.
 */
export function normalizeTexSymbols(text) {
    if (typeof text !== 'string' || text.length === 0) return text;
    if (text.indexOf('\\') === -1) return text;     // no command can exist

    return text
        .split(CODE_SEGMENT_RE)
        .map((segment, index) => {
            // Odd indices are the captured code delimiters — never rewritten.
            if (index % 2 === 1) return segment;
            return replaceCommands(unwrapInlineMath(segment));
        })
        .join('');
}

export default normalizeTexSymbols;
